import { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Glasses, X } from 'lucide-react';
import { UserScope } from '@optiflow/types';
import { useAuthStore } from '@/store/authStore';
import { useUiStore } from '@/store/uiStore';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/utils/cn';
import { visibleNavigation, type NavItem, type NavLeaf } from './navigation';
import { NavIcon } from './NavIcon';

const LINK_BASE =
  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors';
const LINK_ACTIVE = 'bg-brand-50 text-brand-700';
const LINK_IDLE = 'text-muted hover:bg-canvas hover:text-ink';

function ComingSoonTag() {
  const { t } = useTranslation();

  return (
    <span className="text-muted bg-canvas shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium">
      {t('common.comingSoon')}
    </span>
  );
}

function LeafLink({ leaf, onNavigate }: { leaf: NavLeaf; onNavigate: () => void }) {
  const { t } = useTranslation();

  return (
    <NavLink
      to={leaf.to}
      end
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(LINK_BASE, 'ps-11 pe-3 py-2', isActive ? LINK_ACTIVE : LINK_IDLE)
      }
    >
      <span className="flex-1 truncate">{t(leaf.labelKey)}</span>
      {leaf.comingSoon ? <ComingSoonTag /> : null}
    </NavLink>
  );
}

function NavGroup({
  item,
  isExpanded,
  onToggle,
  onNavigate,
}: {
  item: NavItem;
  isExpanded: boolean;
  onToggle: () => void;
  onNavigate: () => void;
}) {
  const { t } = useTranslation();
  const label = t(item.labelKey);

  // A direct link renders as a leaf with an icon.
  if (!item.children) {
    return (
      <li>
        <NavLink
          to={item.to ?? '/'}
          end={item.to === '/'}
          onClick={onNavigate}
          className={({ isActive }) => cn(LINK_BASE, isActive ? LINK_ACTIVE : LINK_IDLE)}
        >
          <NavIcon name={item.icon} className="size-4.5 shrink-0" />
          <span className="flex-1 truncate">{label}</span>
          {item.comingSoon ? <ComingSoonTag /> : null}
        </NavLink>
      </li>
    );
  }

  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isExpanded}
        className={cn(LINK_BASE, LINK_IDLE, 'w-full text-start')}
      >
        <NavIcon name={item.icon} className="size-4.5 shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        <ChevronDown
          className={cn('size-4 shrink-0 transition-transform', isExpanded && 'rotate-180')}
          aria-hidden
        />
      </button>

      {isExpanded ? (
        <ul className="mt-1 space-y-0.5">
          {item.children.map((leaf) => (
            <li key={leaf.key}>
              <LeafLink leaf={leaf} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * Primary navigation.
 *
 * Spacing is entirely logical (`ps-*`, `border-e`, `start-0`), so the whole panel moves to the
 * right in Arabic with no direction-specific rule and no duplicated RTL component.
 */
export function Sidebar() {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const location = useLocation();
  const scope = useAuthStore((state) => state.user?.scope);
  const mobileNavOpen = useUiStore((state) => state.mobileNavOpen);
  const setMobileNavOpen = useUiStore((state) => state.setMobileNavOpen);

  const sections = useMemo(
    () => visibleNavigation(can, scope === UserScope.PLATFORM),
    [can, scope],
  );

  const [expanded, setExpanded] = useState<string[]>([]);

  // Open the group that owns the current route, so a deep link never lands on a collapsed menu.
  useEffect(() => {
    const owner = sections
      .flatMap((section) => section.items)
      .find((item) => item.children?.some((leaf) => location.pathname.startsWith(leaf.to)));

    if (owner) {
      setExpanded((current) => (current.includes(owner.key) ? current : [...current, owner.key]));
    }
  }, [location.pathname, sections]);

  const toggle = (key: string) =>
    setExpanded((current) =>
      current.includes(key) ? current.filter((item) => item !== key) : [...current, key],
    );

  const closeMobileNav = () => setMobileNavOpen(false);

  return (
    <>
      {mobileNavOpen ? (
        <button
          type="button"
          aria-label={t('common.closeNavigation')}
          className="fixed inset-0 z-30 bg-black/40 lg:hidden"
          onClick={closeMobileNav}
        />
      ) : null}

      <aside
        className={cn(
          'border-line fixed inset-y-0 start-0 z-40 flex w-64 flex-col border-e bg-white',
          'transition-transform duration-200 lg:static lg:z-auto',
          // The off-canvas transform is confined to below `lg`. Tailwind v4 emits translate
          // utilities as the `translate` property, so a `lg:` override cannot reliably beat an
          // `rtl:` one by source order — scoping the variant is what actually keeps the desktop
          // sidebar on screen in Arabic.
          !mobileNavOpen && 'max-lg:ltr:-translate-x-full max-lg:rtl:translate-x-full',
        )}
      >
        <div className="border-line flex h-16 shrink-0 items-center justify-between gap-2 border-b px-5">
          <div className="flex min-w-0 items-center gap-2.5">
            <span className="bg-brand-600 grid size-9 shrink-0 place-items-center rounded-lg text-white">
              <Glasses className="size-5" aria-hidden />
            </span>
            <span className="min-w-0">
              <span className="text-ink block truncate text-base leading-tight font-semibold">
                {t('common.appName')}
              </span>
              {/* Wraps rather than truncates: a clipped tagline on the brand block reads as a bug. */}
              <span className="text-muted block text-[10px] leading-snug text-balance">
                {t('common.tagline')}
              </span>
            </span>
          </div>

          <button
            type="button"
            className="text-muted hover:text-ink shrink-0 lg:hidden"
            aria-label={t('common.closeNavigation')}
            onClick={closeMobileNav}
          >
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <nav aria-label={t('nav.primary')} className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          {sections.map((section) => (
            <div key={section.key}>
              {section.labelKey ? (
                <p className="text-muted px-2 pb-2 text-xs font-semibold tracking-wide uppercase">
                  {t(section.labelKey)}
                </p>
              ) : null}

              <ul className="space-y-1">
                {section.items.map((item) => (
                  <NavGroup
                    key={item.key}
                    item={item}
                    isExpanded={expanded.includes(item.key)}
                    onToggle={() => toggle(item.key)}
                    onNavigate={closeMobileNav}
                  />
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="border-line text-muted shrink-0 border-t px-5 py-3 text-xs">
          {t('common.versionLine', { version: '1.0.0' })}
        </div>
      </aside>
    </>
  );
}
